import { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import contractService from '@/services/contractService';
import { Contrato } from '@/types';
import { useDialog } from '@/hooks/useDialog';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  contract: Contrato | null;
  onContractUpdated: () => void;
};

export default function UpdateDiaPagoModal({ isOpen, onClose, contract, onContractUpdated }: Props) {
  const { showAlert, showConfirm } = useDialog();
  const [form, setForm] = useState({
    dia_pago: contract?.dia_pago?.toString() || '',
    propagate: false,
  });
  const [submitting, setSubmitting] = useState(false);

  if (!contract) return null;

  const handleSubmit = async () => {
    const diaPago = parseInt(form.dia_pago);

    if (!diaPago || diaPago < 1 || diaPago > 31) {
      showAlert({
        type: 'warning',
        title: 'Valor inválido',
        message: 'Por favor ingrese un día de pago válido (1-31)',
      });
      return;
    }

    if (diaPago === contract.dia_pago) {
      const confirmed = await showConfirm({
        title: 'Mismo día de pago',
        message: 'El día de pago es el mismo. ¿Desea continuar de todas formas?',
        type: 'info',
      });
      if (!confirmed) return;
    }

    const confirmMessage = form.propagate
      ? `¿Está seguro que desea actualizar el día de pago a ${diaPago} y propagar el cambio a todos los pagos pendientes futuros?`
      : `¿Está seguro que desea actualizar el día de pago a ${diaPago}? Los pagos existentes NO se modificarán.`;

    const confirmed = await showConfirm({
      title: 'Confirmar actualización',
      message: confirmMessage,
      type: 'warning',
      confirmText: 'Sí, actualizar',
    });
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const response = await contractService.updateDiaPago(Number(contract.id), {
        dia_pago: diaPago,
        propagate: form.propagate,
      });

      const affectedCount = (response as any)?.data?.affected_payments_count || 0;

      if (form.propagate && affectedCount > 0) {
        showAlert({
          type: 'success',
          title: 'Éxito',
          message: `Día de pago actualizado exitosamente. Se modificaron ${affectedCount} pagos pendientes.`,
        });
      } else {
        showAlert({
          type: 'success',
          title: 'Éxito',
          message: 'Día de pago actualizado exitosamente.',
        });
      }

      setForm({ dia_pago: '', propagate: false });
      onContractUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating payment day:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al actualizar el día de pago',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Actualizar Día de Pago" size="md">
      <div className="space-y-4">
        {/* Current Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <h4 className="font-medium text-gray-900 mb-2">Información Actual</h4>
          <div className="text-sm">
            <p className="text-gray-700">
              Día de Pago Actual: <span className="font-semibold text-gray-900">{contract.dia_pago}</span>
            </p>
            <p className="text-gray-700 mt-1">
              Contrato: <span className="font-semibold text-gray-900">{contract.numero_contrato}</span>
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-3">
          <Input
            label="Nuevo Día de Pago"
            type="number"
            min="1"
            max="31"
            value={form.dia_pago}
            onChange={(e) => setForm({ ...form, dia_pago: e.target.value })}
            placeholder="1-31"
            required
            helperText="Ingrese un número del 1 al 31"
          />

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="propagate"
                type="checkbox"
                checked={form.propagate}
                onChange={(e) => setForm({ ...form, propagate: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="propagate" className="font-medium text-gray-700 cursor-pointer">
                Propagar a pagos pendientes
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Si está marcado, se actualizará la fecha límite de todos los pagos pendientes (no pagados)
                con el nuevo día de pago.
              </p>
            </div>
          </div>
        </div>

        {/* Warning Messages */}
        {form.propagate ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              <strong>Advertencia:</strong> Al marcar "Propagar a pagos pendientes", se modificarán las fechas
              de vencimiento de todos los pagos que aún no han sido pagados. Los pagos ya registrados como PAGADO
              no se verán afectados.
            </p>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> El día de pago del contrato se actualizará, pero los pagos pendientes existentes
              mantendrán sus fechas límite actuales. Solo los nuevos pagos generados utilizarán el nuevo día.
            </p>
          </div>
        )}

        {/* Day of Month Edge Cases Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <p className="text-xs text-gray-600">
            <strong>Nota sobre días de mes:</strong> Si el día seleccionado no existe en un mes determinado
            (ej: día 31 en febrero), se utilizará el último día válido de ese mes automáticamente.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t">
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Actualizando...' : 'Actualizar Día de Pago'}
          </Button>
          <Button onClick={onClose} variant="secondary">
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
